import { useCallback, useEffect, useState } from 'react';
import { UmsatzWert } from '@/components/UmsatzSperre';
import { Link, useNavigate } from 'react-router-dom';
import { vertriebAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Handshake, RefreshCw, Loader2, PhoneCall, MessageCircle, UserCheck, UserX,
  Calculator, CalendarClock, CheckCircle2, ChevronRight, ChevronDown, X, Sparkles, Info,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

/* Closer-Tagesansicht — "Abschluss heute" (Paket P2 aus dem Setter-Closer-Playbook).
 *
 * Zwei Listen, eine Pflicht:
 *   1. Heutige Probetrainings — begrüßen, danach Erschienen / No-Show setzen.
 *      No-Show geht SOFORT zurück an den Setter (Wiedervorlage jetzt fällig).
 *   2. Gespräche offen — jeder Erschienene ohne erfasstes Ergebnis, auch aus
 *      den Vortagen. Kein Gespräch endet ohne Ergebnis (Ergebnispflicht).
 *
 * Ergebnis "Folgetermin" legt automatisch die Nachfass-Strecke an:
 * 48 h Anruf → 7 Tage WhatsApp → 30 Tage Wiedervorlage (Follow-ups).
 */

const QUALI_LABEL = {
  kind: 'Kind', erwachsener: 'Erwachsener', frau: 'Frau',
  fitness: 'Fitness', selbstverteidigung: 'Selbstverteidigung', wettkampf: 'Wettkampf',
  abnehmen: 'Abnehmen', ausgleich: 'Ausgleich', sonstiges: 'Sonstiges',
  vormittags: 'vormittags', nachmittags: 'nachmittags', abends: 'abends',
  wochenende: 'Wochenende', flexibel: 'flexibel',
  sofort: 'sofort', diesen_monat: 'diesen Monat', unverbindlich: 'unverbindlich',
  macht_es: 'Macher: ja', unsicher: 'Macher: unsicher', eher_nicht: 'Macher: eher nicht',
  selbst: 'entscheidet selbst', partner: 'Partner entscheidet mit', eltern: 'Eltern entscheiden',
};

const GRUND_LABEL = {
  preis: 'Preis', bedenkzeit: 'Bedenkzeit', partner_fragen: 'Partner fragen',
  zeit: 'Keine Zeit', anderes_angebot: 'Anderes Angebot',
  gesundheit: 'Gesundheitlich', sonstiges: 'Sonstiges',
};

const ERGEBNISSE = [
  { id: 'abschluss', titel: 'Abschluss', sub: 'Vertrag heute — Tag-1-Preis', farbe: 'border-emerald-300 text-emerald-700 bg-emerald-50' },
  { id: 'folgetermin', titel: 'Folgetermin', sub: 'Überlegt noch — Strecke startet', farbe: 'border-amber-300 text-amber-700 bg-amber-50' },
  { id: 'kein_interesse', titel: 'Kein Interesse', sub: 'Verloren, mit Grund', farbe: 'border-red-300 text-red-700 bg-red-50' },
];

/* Der Sprung ins Verkaufsgespraech: der Rechner startet mit Name, Kontakt
 * und Zielgruppe des Neukunden vorbefuellt — niemand tippt am Tablet ab. */
function verkaufsLink(lead) {
  const q = lead.qualifizierung || {};
  const zielgruppe = q.fuer_wen === 'kind' ? 'kind' : 'erwachsener';
  const p = new URLSearchParams({
    lead: lead.id || '',      // der Rechner lädt den Lead und füllt Kontakt, Adresse und Tag-1-Angebot selbst (30.10.2026)
    name: lead.name || '', telefon: lead.telefon || '', email: lead.email || '',
    zielgruppe,
  });
  return `/verkauf?${p.toString()}`;
}

function startZeit(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
}

function QualiChips({ q }) {
  if (!q) return <span className="text-[11px] text-amber-600">nicht qualifiziert — vor dem Gespräch kurz nachholen</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {['fuer_wen', 'ziel', 'macher', 'zeitfenster', 'entscheider', 'dringlichkeit'].map((k) => q[k] && (
        <Badge key={k} variant="secondary" className="text-[10px]">{QUALI_LABEL[q[k]] || q[k]}</Badge>
      ))}
    </span>
  );
}

/* Eingebettet im Empfangs-Tablet (onVerkauf gesetzt) bleibt der ganze
 * Ablauf im Empfang: der Sprung ins Verkaufsgespraech wechselt dort nur
 * das Werkzeug, statt die Seite zu verlassen. */
export default function CloserPage({ onVerkauf }) {
  const navigate = useNavigate();
  const [daten, setDaten] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const [sende, setSende] = useState(null);
  const [dialog, setDialog] = useState(null);     // { lead } -> Ergebnis-Dialog
  const [ergebnis, setErgebnis] = useState(null);
  const [grund, setGrund] = useState(null);
  const [notiz, setNotiz] = useState('');
  // v5 (M5) Gespraechsmaske: Pflicht fuer den automatischen Angebotsentwurf
  const [maske, setMaske] = useState({ ziel: '', frequenz: '', tarifwunsch: 'unklar', budget_hinweis: '', entscheider_anwesend: null, einwaende: [] });
  const [briefing, setBriefing] = useState(null);   // lead.id -> Briefing offen

  const [pipeline, setPipeline] = useState(null);
  const [entscheide, setEntscheide] = useState(null); // angebot_id im Dialog

  const laden = useCallback(async () => {
    try {
      const res = await vertriebAPI.closer();
      setDaten(res.data);
    } catch {
      toast.error('Tagesansicht konnte nicht geladen werden');
    } finally {
      setLaedt(false);
    }
    try {
      const a = await vertriebAPI.angebote('offen');
      setPipeline(a.data);
    } catch { /* Pipeline-Block zeigt dann den Ladefehler-Zustand */ }
  }, []);

  useEffect(() => { laden(); }, [laden]);
  useEffect(() => {
    const t = setInterval(laden, 60000);
    return () => clearInterval(t);
  }, [laden]);

  const anwesenheit = async (lead, status) => {
    setSende(lead.id);
    try {
      const res = await vertriebAPI.anwesenheit(lead.id, { status });
      toast.success(res.data.message);
      if (status === 'erschienen') {
        setDialog({ lead });
        setErgebnis(null); setGrund(null); setNotiz('');
      }
      laden();
    } catch {
      toast.error('Konnte nicht gespeichert werden');
    } finally {
      setSende(null);
    }
  };

  const gespraechSpeichern = async () => {
    if (!ergebnis) { toast.error('Bitte ein Ergebnis wählen'); return; }
    if (ergebnis !== 'abschluss' && !grund) { toast.error('Bitte den Grund wählen — „warum heute nicht" ist Pflicht'); return; }
    // Abschluss gibt es nur mit Vertrag (30.10.2026): statt zu speichern öffnet der Verkaufsrechner mit dem Lead.
    if (ergebnis === 'abschluss') {
      const lead = dialog.lead; setDialog(null);
      if (onVerkauf) onVerkauf(lead); else navigate(verkaufsLink(lead));
      return;
    }
    try {
      const res = await vertriebAPI.gespraech(dialog.lead.id, { ergebnis, grund, notiz, ...maske, entscheider_anwesend: maske.entscheider_anwesend });
      toast.success(res.data.message);
      const ang = res.data.angebot;
      if (ang?.angebot_id) toast.success('Angebotsentwurf liegt in der Freigabe-Zentrale', { action: { label: 'Öffnen', onClick: () => navigate('/freigabe', { state: { freigabe_id: ang.freigabe_id } }) } });
      else if (ang?.fehlt) toast.warning(`Kein Angebotsentwurf: ${ang.fehlt}`);
      setMaske({ ziel: '', frequenz: '', tarifwunsch: 'unklar', budget_hinweis: '', entscheider_anwesend: null, einwaende: [] });
      setDialog(null);
      laden();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ergebnis konnte nicht gespeichert werden');
    }
  };

  if (laedt) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  if (!daten) return null;
  const k = daten.kennzahlen;

  /* Das Briefing: alles, was der Closer VOR dem Gespraech wissen sollte —
   * Setter-Qualifizierung, Herkunft, Funnel-Antworten, Lead-Alter. */
  const Briefing = ({ lead }) => {
    const q = lead.qualifizierung;
    const eingegangen = lead.minuten_seit_eingang != null
      ? (lead.minuten_seit_eingang < 60 * 24
        ? `heute` : `vor ${Math.floor(lead.minuten_seit_eingang / (60 * 24))} Tagen`)
      : '—';
    return (
      <div className="w-full bg-muted/40 rounded-xl px-4 py-3 mt-1 text-xs space-y-1.5" data-testid={`briefing-${lead.id}`}>
        <div className="font-bold text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Info className="w-3.5 h-3.5 text-marke" /> Briefing</div>
        <div><span className="text-muted-foreground">Eingegangen:</span> {eingegangen} · <span className="text-muted-foreground">Quelle:</span> {lead.quelle || 'unbekannt'}</div>
        {q ? (
          <div className="flex items-center gap-2 flex-wrap"><span className="text-muted-foreground">Vom Setter:</span> <QualiChips q={q} />{q.notiz && <span>· „{q.notiz}"</span>}</div>
        ) : (
          <div className="text-amber-600">Keine Qualifizierung — die zwei wichtigsten Fragen (Ziel? Wie dringend?) im Gespräch nachholen.</div>
        )}
        {lead.notes && <div className="whitespace-pre-line text-muted-foreground">{lead.notes}</div>}
        <div className="pt-1">
          {onVerkauf ? (
            <button onClick={() => onVerkauf(lead)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-marke text-white font-bold hover:bg-marke-dunkel" data-testid={`btn-verkauf-${lead.id}`}>
              <Calculator className="w-3.5 h-3.5" /> Verkaufsgespräch starten <ChevronRight className="w-3 h-3" />
            </button>
          ) : (
            <Link to={verkaufsLink(lead)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-marke text-white font-bold hover:bg-marke-dunkel" data-testid={`btn-verkauf-${lead.id}`}>
              <Calculator className="w-3.5 h-3.5" /> Verkaufsgespräch starten <ChevronRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      </div>
    );
  };

  const Kontakt = ({ lead }) => (
    <span className="flex items-center gap-1.5">
      {lead.telefon && (
        <a href={`tel:${lead.telefon}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-muted">
          <PhoneCall className="w-3.5 h-3.5" /> {lead.telefon}
        </a>
      )}
      {lead.whatsapp_link && (
        <a href={lead.whatsapp_link} target="_blank" rel="noreferrer" className="inline-flex items-center px-2.5 py-1.5 rounded-lg border border-border text-xs hover:bg-muted" title="WhatsApp öffnen">
          <MessageCircle className="w-3.5 h-3.5" />
        </a>
      )}
    </span>
  );

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="closer-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><Handshake className="w-6 h-6 text-marke" /> Abschluss heute</h1>
          <p className="text-sm text-muted-foreground">Begrüßen, trainieren lassen, direkt danach 15 Minuten zusammensetzen — kein Gespräch ohne Ergebnis.</p>
        </div>
        <Button variant="outline" size="sm" onClick={laden}><RefreshCw className="w-4 h-4 mr-1" /> Aktualisieren</Button>
      </div>

      {/* Tageszahlen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Probetrainings heute</div>
          <div className="text-2xl font-extrabold">{k.heute_geplant}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><UserCheck className="w-3.5 h-3.5" /> Erschienen</div>
          <div className="text-2xl font-extrabold">{k.heute_erschienen}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><Handshake className="w-3.5 h-3.5" /> Gespräche offen</div>
          <div className="text-2xl font-extrabold">{k.gespraeche_offen}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Abschlüsse heute</div>
          <div className="text-2xl font-extrabold text-emerald-600">{k.abschluesse_heute}</div>
        </CardContent></Card>
      </div>

      {/* Heutige Probetrainings */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border/60">
            <h2 className="text-sm font-bold">Probetrainings heute</h2>
          </div>
          {daten.heute.length === 0
            ? <div className="px-4 py-8 text-center text-sm text-muted-foreground">Heute keine Probetrainings.</div>
            : daten.heute.map((l) => {
              const pt = l.probetraining || {};
              const offen = pt.status === 'geplant';
              return (
                <div key={l.id} className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-border/50 last:border-b-0" data-testid={`closer-heute-${l.id}`}>
                  <div className="min-w-[220px] flex-1">
                    <div className="font-bold text-sm flex items-center gap-2">
                      <span className="font-mono text-marke">{startZeit(pt.start)}</span>
                      {l.name || 'Ohne Namen'}
                      {pt.status === 'erschienen' && <Badge variant="secondary" className="text-[10px] bg-emerald-50 text-emerald-700">erschienen</Badge>}
                      {pt.status === 'nicht_erschienen' && <Badge variant="secondary" className="text-[10px] bg-red-50 text-red-600">No-Show</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                      <span>{pt.kurs || 'Kurs?'}</span>
                      <QualiChips q={l.qualifizierung} />
                    </div>
                  </div>
                  <Kontakt lead={l} />
                  <button onClick={() => setBriefing(briefing === l.id ? null : l.id)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-muted" data-testid={`btn-briefing-${l.id}`}>
                    <Info className="w-3.5 h-3.5" /> Briefing <ChevronDown className={`w-3 h-3 transition-transform ${briefing === l.id ? 'rotate-180' : ''}`} />
                  </button>
                  {offen && (
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" variant="outline" disabled={sende === l.id} onClick={() => anwesenheit(l, 'erschienen')} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="btn-erschienen">
                        <UserCheck className="w-3.5 h-3.5 mr-1" /> Erschienen
                      </Button>
                      <Button size="sm" variant="outline" disabled={sende === l.id} onClick={() => anwesenheit(l, 'no_show')} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="btn-noshow">
                        <UserX className="w-3.5 h-3.5 mr-1" /> No-Show
                      </Button>
                    </div>
                  )}
                  {pt.status === 'erschienen' && !l.gespraech && (
                    <Button size="sm" onClick={() => { setDialog({ lead: l }); setErgebnis(null); setGrund(null); setNotiz(''); }} className="bg-marke hover:bg-marke-dunkel" data-testid="btn-ergebnis">
                      <Handshake className="w-3.5 h-3.5 mr-1" /> Ergebnis erfassen
                    </Button>
                  )}
                  {briefing === l.id && <Briefing lead={l} />}
                </div>
              );
            })}
        </CardContent>
      </Card>

      {/* Gespräche offen (auch Vortage) */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between">
            <h2 className="text-sm font-bold">Gespräche offen</h2>
            <span className="text-xs text-muted-foreground">Erschienene ohne Ergebnis — auch aus den Vortagen</span>
          </div>
          {daten.gespraech_offen.length === 0
            ? <div className="px-4 py-8 text-center text-sm text-muted-foreground">Alles erfasst. 👊</div>
            : daten.gespraech_offen.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-border/50 last:border-b-0" data-testid={`closer-offen-${l.id}`}>
                <div className="min-w-[220px] flex-1">
                  <div className="font-bold text-sm">{l.name || 'Ohne Namen'}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                    <span>{(l.probetraining || {}).kurs} · {new Date((l.probetraining || {}).start).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}</span>
                    {l.uebergabe && (
                      /* Die fuenf Uebergabefelder — in 30 Sekunden lesbar,
                         bevor man sich zu jemandem an den Tisch setzt. */
                      <span className="block text-[11px] text-muted-foreground mt-0.5">
                        {l.uebergabe.anlass} · Frage: {l.uebergabe.wichtigste_frage}
                        {l.uebergabe.einwand !== 'keiner' && <> · Einwand: {GRUND_LABEL[l.uebergabe.einwand] || l.uebergabe.einwand}</>}
                      </span>
                    )}
                    <QualiChips q={l.qualifizierung} />
                  </div>
                </div>
                <Kontakt lead={l} />
                <button onClick={() => setBriefing(briefing === l.id ? null : l.id)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-muted">
                  <Info className="w-3.5 h-3.5" /> Briefing <ChevronDown className={`w-3 h-3 transition-transform ${briefing === l.id ? 'rotate-180' : ''}`} />
                </button>
                <Button size="sm" onClick={() => { setDialog({ lead: l }); setErgebnis(null); setGrund(null); setNotiz(''); }} className="bg-marke hover:bg-marke-dunkel" data-testid="btn-ergebnis-offen">
                  <Handshake className="w-3.5 h-3.5 mr-1" /> Ergebnis erfassen
                </Button>
                {briefing === l.id && <Briefing lead={l} />}
              </div>
            ))}
        </CardContent>
      </Card>

      {/* Ergebnis-Dialog */}
      {dialog && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDialog(null)}>
          <div className="bg-background rounded-2xl w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="gespraech-dialog">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold">Abschlussgespräch: {dialog.lead.name || 'Lead'}</h3>
                <p className="text-xs text-muted-foreground">Ergebnis ist Pflicht — bei „nicht heute" auch der Grund.</p>
              </div>
              <button onClick={() => setDialog(null)} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
            </div>

            {dialog.lead.qualifizierung && (
              <div className="bg-muted/50 rounded-xl px-3 py-2 text-xs flex items-center gap-2 flex-wrap">
                <Sparkles className="w-3.5 h-3.5 text-marke" /> <QualiChips q={dialog.lead.qualifizierung} />
                {dialog.lead.qualifizierung.notiz && <span className="text-muted-foreground">· {dialog.lead.qualifizierung.notiz}</span>}
              </div>
            )}

            <div className="grid grid-cols-3 gap-2">
              {ERGEBNISSE.map((e) => (
                <button key={e.id} onClick={() => { setErgebnis(e.id); if (e.id === 'abschluss') setGrund(null); }}
                  className={`py-3 px-2 rounded-xl border-2 text-xs font-bold transition-all ${ergebnis === e.id ? e.farbe + ' border-current' : 'border-border/60 text-muted-foreground hover:border-border'}`}
                  data-testid={`ergebnis-${e.id}`}>
                  {e.titel}<span className="block text-[9px] font-normal opacity-80 mt-0.5">{e.sub}</span>
                </button>
              ))}
            </div>

            {ergebnis && ergebnis !== 'abschluss' && (
              <div>
                <div className="text-[11px] font-medium text-muted-foreground mb-1.5">Warum heute nicht? (Pflicht)</div>
                <div className="flex flex-wrap gap-2">
                  {daten.gruende.map((g) => (
                    <button key={g} onClick={() => setGrund(g)}
                      className={`px-3 py-2 rounded-xl border-2 text-xs font-bold transition-all ${grund === g ? 'border-marke bg-marke/5 text-marke' : 'border-border/60 text-muted-foreground hover:border-border'}`}
                      data-testid={`grund-${g}`}>
                      {GRUND_LABEL[g] || g}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {ergebnis === 'folgetermin' && (
              <div className="space-y-2 rounded-xl border border-border/60 p-3" data-testid="gespraechsmaske">
                <div className="text-[11px] font-medium text-muted-foreground">Gesprächsmaske — daraus entsteht der Angebotsentwurf</div>
                <input value={maske.ziel} onChange={(e) => setMaske({ ...maske, ziel: e.target.value })} placeholder="Ziel (z. B. fit werden, Selbstverteidigung, Kind: Disziplin)"
                       className="w-full rounded-lg border border-border bg-background px-2.5 py-2 text-sm" data-testid="maske-ziel" />
                <div className="flex flex-wrap gap-1.5 items-center text-xs">
                  <span className="text-muted-foreground mr-1">Training/Woche:</span>
                  {['1x', '2x', 'unbegrenzt', 'unklar'].map((f) => (
                    <button key={f} type="button" onClick={() => setMaske({ ...maske, frequenz: f })}
                      className={`px-2.5 py-1 rounded-lg border ${maske.frequenz === f ? 'border-marke text-marke bg-marke/5' : 'border-border/60 text-muted-foreground'}`} data-testid={`maske-frequenz-${f}`}>{f}</button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1.5 items-center text-xs">
                  <span className="text-muted-foreground mr-1">Entscheider anwesend:</span>
                  {[[true, 'ja'], [false, 'nein']].map(([w, l]) => (
                    <button key={l} type="button" onClick={() => setMaske({ ...maske, entscheider_anwesend: w })}
                      className={`px-2.5 py-1 rounded-lg border ${maske.entscheider_anwesend === w ? 'border-marke text-marke bg-marke/5' : 'border-border/60 text-muted-foreground'}`} data-testid={`maske-entscheider-${l}`}>{l}</button>
                  ))}
                  <span className="text-muted-foreground ml-3 mr-1">Budget:</span>
                  {['frei', 'knapp'].map((b) => (
                    <button key={b} type="button" onClick={() => setMaske({ ...maske, budget_hinweis: maske.budget_hinweis === b ? '' : b })}
                      className={`px-2.5 py-1 rounded-lg border ${maske.budget_hinweis === b ? 'border-marke text-marke bg-marke/5' : 'border-border/60 text-muted-foreground'}`}>{b}</button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1.5 items-center text-xs">
                  <span className="text-muted-foreground mr-1">Tarifwunsch:</span>
                  <select value={maske.tarifwunsch} onChange={(e) => setMaske({ ...maske, tarifwunsch: e.target.value })} className="rounded-lg border border-border bg-background px-2 py-1 text-xs">
                    <option value="unklar">unklar — zwei Optionen</option>
                    <option value="basic">Basic</option><option value="performance">Performance</option><option value="performance_plus">Performance Plus</option>
                    <option value="basic_kids">Basic Kids</option><option value="performance_kids">Performance Kids</option><option value="family">Family</option>
                  </select>
                </div>
              </div>
            )}
            {ergebnis === 'folgetermin' && (
              <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2">Empfehlungs-PDF mitgeben oder per WhatsApp senden. Die Nachfass-Strecke wird automatisch angelegt: <b>48 h Anruf → 7 Tage WhatsApp → 30 Tage Wiedervorlage</b>.</p>
            )}

            <div>
              <div className="text-[11px] font-medium text-muted-foreground mb-1.5">Notiz (optional)</div>
              <textarea value={notiz} onChange={(e) => setNotiz(e.target.value)} rows={2}
                className="w-full rounded-xl border border-border bg-background p-2.5 text-sm" placeholder="z. B. will mit Partnerin am Samstag wiederkommen" />
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Button onClick={gespraechSpeichern} className="flex-1 bg-marke hover:bg-marke-dunkel" data-testid="gespraech-speichern">
                {ergebnis === 'abschluss' ? <><Calculator className="w-4 h-4 mr-1" /> Zum Verkaufsrechner (Vertrag) <ChevronRight className="w-3.5 h-3.5" /></> : 'Speichern'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Meine Pipeline — offene Angebote nach WERT sortiert, nicht nach
          Datum. Rote Zeilen: Wiedervorlage fehlt oder liegt in der
          Vergangenheit (R4). Nach 21 Tagen ohne Bewegung raeumt das System
          selbst auf (kein_entscheid -> Nurture). */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between">
            <h2 className="text-sm font-bold">Meine Pipeline — offene Angebote</h2>
            {pipeline && (
              <span className="text-xs text-muted-foreground">
                {pipeline.anzahl} offen · <b><UmsatzWert wert={pipeline.summe} format={(v) => `${v.toLocaleString('de-DE')} €`} kurz /></b>
              </span>
            )}
          </div>
          {!pipeline ? (
            <p className="p-4 text-sm text-muted-foreground">Lädt …</p>
          ) : pipeline.anzahl === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              Keine offenen Angebote — jedes Beratungsgespräch, das im
              Verkaufsrechner endet, legt hier automatisch eines an.
            </p>
          ) : (
            <div className="divide-y divide-border/50">
              {pipeline.angebote.map((a) => (
                <div key={a.id} className={`px-4 py-3 flex items-center gap-3 flex-wrap ${a.rot ? 'bg-red-50' : ''}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">
                      {a.lead_name}
                      {a.rot && <Badge variant="destructive" className="ml-2 text-[10px]">Wiedervorlage überfällig</Badge>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {a.plan_name || a.plan_id} · {a.laufzeit} ·
                      Wiedervorlage {a.wiedervorlage_am ? new Date(a.wiedervorlage_am).toLocaleDateString('de-DE') : '—'}
                    </p>
                  </div>
                  <span className="font-mono font-bold text-sm">{(a.vertragswert_gesamt || 0).toLocaleString('de-DE')} €</span>
                  <span className="flex gap-1">
                    <Button size="sm" variant="outline" className="h-7 text-emerald-700 border-emerald-200"
                            onClick={async () => {
                              await vertriebAPI.angebotEntscheiden(a.id, { status: 'gewonnen' });
                              toast.success('Gewonnen — Vertrag im Verkaufsrechner anlegen');
                              laden();
                            }}>Gewonnen</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-muted-foreground"
                            onClick={() => setEntscheide(a)}>Verloren</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-muted-foreground"
                            onClick={async () => {
                              const d = new Date(); d.setDate(d.getDate() + 7);
                              await vertriebAPI.angebotEntscheiden(a.id, {
                                status: 'offen', wiedervorlage_am: d.toISOString() });
                              toast.success('Wiedervorlage +7 Tage');
                              laden();
                            }}>+7 Tage</Button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Verlustgrund-Dialog: verloren gibt es nur MIT Grund (R2/Stufe 7) */}
      {entscheide && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
             onClick={() => setEntscheide(null)}>
          <div className="bg-background rounded-2xl p-5 w-full max-w-sm space-y-3"
               onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-sm">Warum verloren? — {entscheide.lead_name}</h3>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(GRUND_LABEL).map(([wert, label]) => (
                <Button key={wert} size="sm" variant="outline"
                        onClick={async () => {
                          await vertriebAPI.angebotEntscheiden(entscheide.id, {
                            status: 'verloren', verlustgrund: wert });
                          toast.success('Als verloren erfasst — danke für den Grund');
                          setEntscheide(null);
                          laden();
                        }}>{label}</Button>
              ))}
            </div>
            <Button variant="ghost" size="sm" className="w-full" onClick={() => setEntscheide(null)}>Abbrechen</Button>
          </div>
        </div>
      )}
    </div>
  );
}
